import { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import FontFaceObserver from "fontfaceobserver"
import { THEMES } from "./Themed"
import { useWindowSize } from "react-use"
import { cn } from "@utils/tw"

const ASCENDERS = "bdfhijkltV".split("")
const DESCENDERS = "gjpqyf".split("")

const hasCharacter = (text1: string, text2: string) => {
  return text1.split("").some((char) => text2.includes(char))
}

const measureText = (el: HTMLSpanElement, text: string, to: "all" | "ascender" | "descender", reverse: boolean) => {
  const canvas = document.createElement("canvas")
  const context = canvas.getContext("2d")
  const searchWith = to === "ascender" ? ASCENDERS : DESCENDERS
  const foundIndexes: number[] = []
  let toMeasure = text.split("")
  const toIndex = toMeasure.length
  if (reverse) toMeasure = toMeasure.reverse()
  toMeasure.forEach((char, index) => {
    if (searchWith.includes(char)) {
      foundIndexes.push(index + (to === "ascender" ? 0 : 3))
    }
  })
  const useIndex = reverse ? foundIndexes[foundIndexes.length - 1] : foundIndexes[0]
  const sliced = toMeasure.slice(0, to === "all" ? toIndex : useIndex)
  if (reverse) sliced.reverse()
  const measurableText = sliced.join("")
  if (!context) return 0
  const computed = window.getComputedStyle(el)
  context.font = `${computed.getPropertyValue("font-weight")} ${computed.getPropertyValue("font-size")} ${computed.getPropertyValue("font-family")}`
  let w = context.measureText(measurableText).width
  if (to === "descender" && measurableText === text) {
    const firstCharW = context.measureText(text[0]).width
    w += firstCharW * 1.5
  }
  return w
}

export const FormattedTitle = ({
  text,
  className,
  theme,
  scale = 1,
}: {
  text: string
  className?: string
  theme?: string
  scale?: number
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [ready, setReady] = useState(false)
  const [arranged, setArranged] = useState(false)
  const animationTimers: number[] = []
  const { width } = useWindowSize()

  useEffect(() => {
    const observer1 = new FontFaceObserver("latino-gothic-variable")
    const observer2 = new FontFaceObserver("filmotype-yukon")
    Promise.all([observer1.load(), observer2.load()]).then(() => {
      setReady(true)
    })
  }, [])

  useEffect(() => {
    if (!ready) return
    const groups = ref.current?.querySelectorAll(".formatted-group")
    if (!groups) return
    let widestLine = 0
    let maxWidth = 0
    const testIsSerif = (el: HTMLElement) => el?.classList?.contains("is-serif")
    groups.forEach((group) => {
      const lines = group.querySelectorAll(".formatted-line")
      let asc = 0
      let desc = 0
      let previousLineWidth = 0
      let previousLineSerif = false
      let previousX = 0
      let previousText = ""
      lines.forEach((line, lx) => {
        const l = line as HTMLElement
        const text = l.textContent || ""
        const inner = l.querySelector(".formatted-line-inner")
        const all = ((inner || l) as HTMLSpanElement).offsetWidth
        const actualWidth = (l as HTMLElement).offsetWidth - all
        const diff = actualWidth - all
        const halfDiff = diff / 2
        const isSerif = testIsSerif(l)
        let x = 0
        if (previousLineSerif) {
          x = hasCharacter(DESCENDERS.join(""), previousText) ? previousX - (all - previousLineWidth) - desc : 0
          l.style.transform = `translateX(${x}px)`
          previousX = x
        }
        if (isSerif) {
          asc = measureText(inner as HTMLSpanElement, text, "ascender", false)
          desc = measureText(inner as HTMLSpanElement, text, "descender", true)
          previousLineSerif = true
          x = previousX + previousLineWidth
          if (lx > 0) {
            x -= asc
          }
          l.style.transform = `translateX(calc(${x}px - 0.5em))`
          previousX = x
        } else {
          previousLineSerif = false
        }
        if (x === 0 && !testIsSerif(lines[lx + 1] as HTMLElement)) {
          l.style.setProperty("align-self", "center")
        }
        previousLineWidth = all
        widestLine = Math.max(widestLine, all)
        previousText = text
        maxWidth = Math.max(maxWidth, all + x)
      })
    })
    ref.current?.style.setProperty("width", `${maxWidth}px`)
    innerRef.current?.style.setProperty("width", `${widestLine}px`)
    setArranged(true)

    return () => {
      groups.forEach((group) => {
        const lines = group.querySelectorAll(".formatted-line")
        lines.forEach((line) => {
          const l = line as HTMLElement
          l.style.transform = ""
          l.style.setProperty("align-self", "unset")
        })
      })
    }
  }, [ready, width, text])

  useEffect(() => {
    if (arranged) animateIn()
  }, [arranged, text])

  if (!text) return null

  const lines = text
    .replace(/&amp;/g, "&")
    .replace(/<br>/g, "\n")
    .replace(/[^\S\r\n]+/g, " ")
    .trim()
    .split("\n")
    .map((line) => line.trim())

  const animateIn = () => {
    if (!arranged) return
    const bgs = ref.current?.querySelectorAll(".formatted-word-bg")
    const fgs = ref.current?.querySelectorAll(".formatted-word-fg")
    animationTimers.forEach((timer) => window.clearTimeout(timer))
    bgs?.forEach((line, lx) => {
      const bgl = bgs[lx] as HTMLElement
      const fgl = fgs?.[lx] as HTMLElement
      bgl.classList.remove("animate")
      fgl.classList.remove("animate")
      animationTimers.push(
        window.setTimeout(() => {
          bgl.classList.add("animate")
        }, lx * 250),
      )
      animationTimers.push(
        window.setTimeout(
          () => {
            fgl.classList.add("animate")
          },
          lx * 250 + 150,
        ),
      )
    })
  }

  return (
    <div
      ref={ref}
      // onMouseEnter={animateIn}
      className={twMerge(
        "flex flex-col items-start type-title-m w-fit relative",
        ready ? "visible" : "invisible",
        className,
      )}
      style={{ transform: `scale(${scale})` }}
    >
      <div ref={innerRef} className="relative shrink-0">
        <div className="formatted-group absolute w-full h-full top-0 left-0 pointer-events-none flex flex-col items-start">
          {lines.map((line, index) => {
            const serif = line.includes("{")
            const text = line.replace(/{/g, "").replace(/}/g, "")
            const words = text.split(" ")
            const bgSize = { x: 0.5, y: 0.5 }

            if (serif) {
              bgSize.y = 0.6
              if (DESCENDERS.includes(words?.[0]?.[0]?.toLowerCase?.())) {
                bgSize.x = 0.75
              }
            }
            return (
              <span
                key={`line-bg-${index}`}
                className={twMerge(
                  "formatted-line formatted-line-bg inline-block w-fit relative shrink-0 whitespace-nowrap",
                  serif && "is-serif type-title-heading-serif px-[0.5em] py-[0.5em] -mt-[0.5em] -mb-[0.5em]",
                  !serif && "mt-[0.05em]",
                )}
              >
                <span className={cn("formatted-line-inner inline-block")}>
                  {words.map((word, i) => {
                    return (
                      <span
                        key={`word-bg-${i}`}
                        className={twMerge(
                          `formatted-word formatted-word-bg relative`,
                          "inline-block whitespace-nowrap",
                          "opacity-0 [&.animate]:opacity-100",
                        )}
                      >
                        <span
                          className={twMerge("absolute inline-block top-0 left-0", theme && THEMES[theme]?.bg)}
                          style={{
                            width: `calc(100% + ${bgSize.x}em)`,
                            height: `calc(100% + ${bgSize.y}em)`,
                            margin: `-${bgSize.y / 2}em 0 0 -${bgSize.x / 2}em`,
                          }}
                        />
                        <span className="opacity-0">{word}</span>
                        {i < line.split(" ").length - 1 && word !== "" && (
                          <span className="inline-block whitespace-pre">&nbsp;</span>
                        )}
                      </span>
                    )
                  })}
                </span>
              </span>
            )
          })}
        </div>
        <div className="formatted-group relative flex flex-col items-start">
          {lines.map((line, index) => {
            const serif = line.includes("{")
            const text = line.replace(/{/g, "").replace(/}/g, "")
            return (
              <span
                key={`line-fg-${index}`}
                className={twMerge(
                  "formatted-line formatted-line-fg inline-block w-fit shrink-0 whitespace-nowrap",
                  serif && "is-serif type-title-heading-serif px-[0.5em] py-[0.5em] -mt-[0.5em] -mb-[0.5em]",
                  !serif && "mt-[0.1em]",
                )}
              >
                <span className={cn("formatted-line-inner inline-block")}>
                  {text.split(" ").map((word, i) => (
                    <span
                      key={`word-fg-${i}`}
                      className={twMerge(
                        `formatted-word formatted-word-fg`,
                        "inline-block whitespace-nowrap",
                        "opacity-0 [&.animate]:opacity-100",
                      )}
                    >
                      {word}
                      {i < line.split(" ").length - 1 && word !== "" && (
                        <span className="inline-block whitespace-pre">&nbsp;</span>
                      )}
                    </span>
                  ))}
                </span>
              </span>
            )
          })}
        </div>
      </div>
    </div>
  )
}
